/**
 * Resume improve agent step graph.
 *
 * Declarative on purpose: because the whole plan is known before the run starts,
 * the UI can render all five rows greyed-out and light them up as they land — a
 * checklist rather than an append-only log.
 *
 * Kept free of any model import so client components can pull the labels and the
 * initial plan without dragging @ai-sdk/openai (and the API key) into the browser
 * bundle. Same constraint as outreach/agent/step-graph.ts.
 */

import { initialSteps } from '@/lib/agent-kit/runner';
import type { AgentGenerationStep, AgentStepCopy } from '@/lib/agent-kit/types';
import type { ResumeImproveStepKey } from './types';

export const resumeImproveStepOrder: ResumeImproveStepKey[] = [
  'ASSESS_CONTENT',
  'CHOOSE_APPROACH',
  'REWRITE',
  'CRITIQUE',
  'POLISH',
];

export const resumeImproveStepDependencies: Record<
  ResumeImproveStepKey,
  ResumeImproveStepKey[]
> = {
  ASSESS_CONTENT: [],
  CHOOSE_APPROACH: ['ASSESS_CONTENT'],
  REWRITE: ['CHOOSE_APPROACH'],
  CRITIQUE: ['REWRITE'],
  POLISH: ['CRITIQUE'],
};

/**
 * User-facing copy. Labels are present-progressive because they are read while
 * the step is running; descriptions explain what the agent decided, not merely
 * that it decided something.
 */
export const resumeImproveStepCopy: AgentStepCopy<ResumeImproveStepKey> = {
  labels: {
    ASSESS_CONTENT: 'Reading what you wrote',
    CHOOSE_APPROACH: 'Deciding what to fix',
    REWRITE: 'Rewriting it',
    CRITIQUE: 'Checking nothing was invented',
    POLISH: 'Fixing what the check found',
  },
  descriptions: {
    ASSESS_CONTENT:
      'Works out what kind of content this is and what is actually weak about it — vague verbs, missing outcomes, passive voice, wrong tense, or simply too long.',
    CHOOSE_APPROACH:
      'Decides what to fix and what to leave alone, sets a length budget, and — critically — establishes whether there are any real numbers to work with. If there are none, it says so, so the rewrite stays honest instead of inventing them.',
    REWRITE: 'Applies that plan.',
    CRITIQUE:
      'A different model checks the rewrite for invented facts and figures, dropped skills, tense, and whether it actually improved anything. A regex pass catches resume clichés and weak verbs on top.',
    POLISH: 'Applies the fixes. Skipped when the rewrite passes.',
  },
};

/** All steps PENDING — the plan, publishable before any work begins. */
export function initialResumeImproveSteps(): AgentGenerationStep<ResumeImproveStepKey>[] {
  return initialSteps(resumeImproveStepOrder);
}

/** Bump when the prompts or the graph change, so stale traces are identifiable. */
export const RESUME_IMPROVE_AGENT_VERSION = 'v1';
